import { TableColumn } from "../../models/components/table/table-column.model";
import { ITablePagination } from "../../models/components/table/table-pagination.model";
import { TableTemplateTranslate } from "../../models/components/table/table-template.model";
import { ParticipantProfile, PrincipalMethod } from "../../models/entities/participant.model";
import { Roles } from "../../models/roles.model";
import { getSessionStorageUser } from "../session/auth.service";

/**
 * View mode: admin/coach
 * @description Returns true if the user is in admin view, and false if it's coach view
 */
const isAdminViewMode = (): boolean =>
	(Roles.isAdmin() || Roles.isProvider()) && getSessionStorageUser()?.adminView;

/**
 * Sort: compound field
 * @description When a table column value is the result of multiple fields,
 * we have to get the multiple fields that compose this column.
 * Ex:
 * Name: first name + last name
 */
const compoundSortField = (
	columns: TableColumn[],
	pagination: ITablePagination
): string | undefined => {
	let compoundSortField: string | undefined = undefined;

	// Find the column:
	const column = columns.find((column) => column.field === pagination.sortField);
	compoundSortField = column?.sortingField ?? pagination.sortField;
	return compoundSortField;
};

const isKannactId = (value: string): boolean =>
	!Number(value) && value?.length >= 6 && value?.length <= 9;

/**
 * Participant ID from Kannact ID
 * @description Given a Kannact ID, get it's ID
 */
const getParticipantId = (kannactId: string) => kannactId.substring(3);

/**
 * Route id for `/participant/:participantId/...`, from whatever a list row carries.
 *
 * Some list payloads carry the internal participant id (alerts do — `Task.participantId`),
 * and some carry only the Kannact ID: the `/coach-appointments` response schema
 * (schedule-appointments → `packages/schemas/shared.schema.ts`, `participantSchema`)
 * has NO `id` field at all, and because it is a Fastify *response* schema an extra key
 * would be stripped even if the upstream sent one. A row built from it therefore never
 * had an id to link with.
 *
 * Deriving it from the Kannact ID is safe and is what the rest of the platform already
 * does (`AppointmentBlockContainer`, `CallPresenter`): a Kannact ID is a 3-character
 * institution code followed by the zero-padded `entity_id`
 * (gladstone-database `V0_3_6__remove_contact_id_from_create_participant.sql`), and the
 * route reads the param with `Number(...)`, so the leading zeros are harmless.
 *
 * Returns null when the row carries neither — the caller renders plain text, never a
 * link to `/participant/undefined`.
 */
const getParticipantRouteId = (
	id?: number | string | null,
	kannactId?: string | null
): string | null => {
	if (id !== undefined && id !== null && `${id}`.length > 0) return `${id}`;
	if (kannactId && kannactId.length > 3) return getParticipantId(kannactId);
	return null;
};

/**
 * Participant: Last Contact Purpose
 */
const formatLastContactPurpose = (value: string): TableTemplateTranslate | undefined => {
	let result: TableTemplateTranslate | undefined;

	// Map value to the only know descriptions
	if (
		value === "NONE" ||
		value === "CALL_LOG_COACHING_PHONE_CALL" ||
		value === "ENGAGE" ||
		value === "ENGAGED"
	) {
		result = { key: `ENUMS.CONTACT_PURPOSE.${value}` };
	} else if (value.startsWith("RO_")) {
		result = {
			key: "ENUMS.CONTACT_PURPOSE.RO_",
			params: { attempt: value.slice(3) },
		};
	} else if (value) {
		result = { key: value };
	}

	return result;
};

// Participant: principal phone
const getPrincipalPhone = (participant: ParticipantProfile, prefix: boolean) => {
	let result: string | null =
		participant.principal?.find((x) => x.method === PrincipalMethod.PHONE)?.username ?? null;
	if (result && !prefix) {
		// Remove prefix
		result = result.replace("+1", "");
	}

	return result;
};

// Participant: principal email
const getPrincipalEmail = (participant: ParticipantProfile) =>
	participant.principal?.find((x) => x.method === PrincipalMethod.EMAIL)?.username ?? null;

// Participant: primary email
const getPrimaryEmail = (participant: ParticipantProfile) =>
	participant.emails?.filter((x) => x.isPrimary)[0]?.emailAddress ?? null;

// Participant: primary phone
const getPrimaryPhone = (participant: ParticipantProfile) => {
	const phone = participant.phones?.filter((x) => x.isPrimary)[0] ?? null;
	if (!phone) {
		return null;
	}

	return `${phone.countryCode}${phone.phoneNumber}`;
};

export const HelperService = {
	compoundSortField,
	isKannactId,
	getParticipantId,
	getParticipantRouteId,
	formatLastContactPurpose,
	isAdminViewMode,
	getPrincipalPhone,
	getPrincipalEmail,
	getPrimaryEmail,
	getPrimaryPhone,
};
